/**
 * useProductionLightbox — the PAGE half of a station's image viewer.
 *
 * `ProductionImageLightbox` renders the overlay. This owns everything the page
 * around it has to get right, and every one of those things was hand-rolled
 * identically on four station pages before it lived here. Four copies of a rule
 * is four chances to drift, and they already had:
 *
 *  1. THE CLICKED TILE'S FALLTHROUGH, APPLIED TO THAT TILE ONLY. `sourceOffset`
 *     is how far the tapped tile had already walked its own chain, and
 *     `sourceOffsetIndex` is the flat-list entry it applies to. Without the
 *     PAIR the full view reopens a url the tile has already watched fail, and
 *     the offset must not travel when the arrows move `index` to another image.
 *  2. OPEN MEANS "OPEN WITH SOMETHING IN IT". `open` is false for an empty
 *     list. Only an overlay that is really trapping focus may pause the
 *     scanner: an "open" overlay over nothing holds the scanner paused with
 *     nothing on screen and the operator's next scan goes nowhere.
 *  3. RESET WHEN THE SUBJECT CHANGES. A garment scanned away, accepted or
 *     cleared takes its images with it. Same failure as 2 if it is skipped.
 *
 * Pass the result's `open` to the overlay AND to every scanner/hotkey pause
 * list (PRODUCTION_DESIGN §6.2): it is the guarded value, so the two can never
 * disagree about whether anything is trapping focus.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ProductionLightboxImage } from './ProductionImageLightbox';
import type { ProductionLightboxEntry } from './ProductionPreviewTile';

interface LightboxState {
  open: boolean;
  index: number;
  /** The flat-list entry `sourceOffset` applies to. Null = no offset. */
  sourceOffsetIndex: number | null;
  sourceOffset: number;
}

/** Shared identity, so a reset on an already-closed page costs no render. */
const CLOSED: LightboxState = { open: false, index: 0, sourceOffsetIndex: null, sourceOffset: 0 };

export interface UseProductionLightboxArgs {
  /** The flat list, from `buildProductionLightboxData`. Memoise it. */
  entries: ReadonlyArray<ProductionLightboxEntry>;
  /** Tile key to flat index, from the same call. */
  indexByKey: ReadonlyMap<string, number>;
  /**
   * Identity of what is on screen: the garment id, the order id, the batch id.
   * When it changes the overlay closes, because its images are gone.
   */
  resetKey: string | null | undefined;
}

export interface ProductionLightboxController {
  /** True ONLY when the overlay is open and has at least one image in it. */
  open: boolean;
  index: number;
  images: ProductionLightboxImage[];
  /**
   * Open at the tile with this key. `sourceIndex` is the chain position the
   * tile ACTUALLY rendered, straight from the tile's own `onOpen`.
   */
  openAt: (key: string, sourceIndex: number) => void;
  /** For the overlay's `onOpenChange`. */
  setOpen: (open: boolean) => void;
  /** For the overlay's `onIndexChange` (the arrows and the arrow keys). */
  setIndex: (index: number) => void;
}

export function useProductionLightbox({
  entries,
  indexByKey,
  resetKey,
}: UseProductionLightboxArgs): ProductionLightboxController {
  const [state, setState] = useState<LightboxState>(CLOSED);

  const images = useMemo<ProductionLightboxImage[]>(
    () =>
      entries.map((entry, entryIndex) => ({
        kind: entry.kind,
        subject: entry.subject,
        // Rule 1: the offset belongs to ONE entry, and never runs off the end
        // of a chain that has since been rebuilt shorter.
        sources: entry.sources.slice(
          Math.max(
            entryIndex === state.sourceOffsetIndex
              ? Math.min(state.sourceOffset, entry.sources.length - 1)
              : 0,
            0,
          ),
        ),
        thumbUrl: entry.thumbUrl,
      })),
    [entries, state.sourceOffsetIndex, state.sourceOffset],
  );

  // Rule 3.
  useEffect(() => {
    setState((current) => (current === CLOSED ? current : CLOSED));
  }, [resetKey]);

  const openAt = useCallback(
    (key: string, sourceIndex: number) => {
      const index = indexByKey.get(key);
      if (index === undefined) return;
      setState({
        open: true,
        index,
        sourceOffsetIndex: sourceIndex > 0 ? index : null,
        sourceOffset: sourceIndex > 0 ? sourceIndex : 0,
      });
    },
    [indexByKey],
  );

  const setOpen = useCallback((open: boolean) => {
    setState((current) => (open === current.open ? current : { ...current, open }));
  }, []);

  const setIndex = useCallback((index: number) => {
    setState((current) => ({ ...current, open: true, index }));
  }, []);

  return {
    // Rule 2.
    open: state.open && images.length > 0,
    index: state.index,
    images,
    openAt,
    setOpen,
    setIndex,
  };
}
